import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Wallet, Clock, TrendingUp, CreditCard, Building2, Plus, Pencil, Banknote, X, Zap, CheckCircle2, AlertCircle, Globe, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { Loading, PageHead, Empty, money } from "@/components/common";
import { StatusPill } from "@/components/StatusPill";
import { countryName, PAYOUT_COUNTRIES } from "@/lib/countries";

const PAYOUT_COUNTRY_NAMES = PAYOUT_COUNTRIES.map((c) => c.name).join(", ");

function ConnectCountryModal({ onClose, onContinue }) {
  const [country, setCountry] = useState("US");
  const [busy, setBusy] = useState(false);
  const go = async () => { setBusy(true); await onContinue(country); setBusy(false); };
  return (
    <div className="ss-modal-back" onClick={onClose}>
      <div className="ss-modal" onClick={(e) => e.stopPropagation()} data-testid="connect-country-modal">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <h2 style={{ fontSize: 20 }}>Where do you get paid?</h2>
          <button className="disc-search" style={{ width: 34, height: 34 }} onClick={onClose} data-testid="connect-country-close"><X size={18} /></button>
        </div>
        <div className="ss-muted" style={{ fontSize: 14, marginBottom: 16 }}>
          Choose the country of your bank account. This is set once and <b>can't be changed later</b>, so pick the
          country where you'll receive payouts.
        </div>
        <label className="ss-label">Payout country</label>
        <div className="ss-field" style={{ height: 48, marginBottom: 18 }}>
          <Globe size={16} className="ss-muted" />
          <select value={country} onChange={(e) => setCountry(e.target.value)} data-testid="connect-country-select"
            style={{ border: "none", background: "transparent", outline: "none", flex: 1, fontSize: 15, cursor: "pointer" }}>
            {PAYOUT_COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>
        </div>
        <div className="ss-muted" style={{ fontSize: 12.5, lineHeight: 1.6, marginBottom: 16, padding: "10px 12px", background: "var(--surface-3, #f0eee6)", borderRadius: 10 }} data-testid="payout-country-disclaimer">
          Only the {PAYOUT_COUNTRIES.length} countries Stripe supports for seller payouts are listed above. A few places we ship to — including
          <b> India</b> and <b>Lebanon</b> — aren't supported for payouts yet. Your country is <b>set once and can't be changed</b> afterwards.
          <details style={{ marginTop: 6 }}>
            <summary style={{ cursor: "pointer", color: "var(--brand)", fontWeight: 700 }} data-testid="modal-countries-toggle">See all supported countries</summary>
            <span style={{ display: "block", marginTop: 6 }}>{PAYOUT_COUNTRY_NAMES}.</span>
          </details>
        </div>
        <button className="ss-btn ss-btn-primary ss-btn-block" onClick={go} disabled={busy} data-testid="connect-country-continue">
          <Zap size={16} /> {busy ? "Opening Stripe…" : "Continue to Stripe"}
        </button>
      </div>
    </div>
  );
}

function PayoutModal({ existing, onClose, onSaved }) {
  const [method, setMethod] = useState(existing?.method || "bank");
  const [accountName, setAccountName] = useState(existing?.account_name || "");
  const [bankName, setBankName] = useState(existing?.bank_name || "");
  const [accountNumber, setAccountNumber] = useState("");
  const [paypalEmail, setPaypalEmail] = useState(existing?.paypal_email || "");
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { method, account_name: accountName };
      if (method === "bank") { body.bank_name = bankName; body.account_number = accountNumber; }
      else { body.paypal_email = paypalEmail; }
      const res = await api.post("/seller/payout", body);
      toast.success("Payout account saved");
      onSaved(res.payout_account);
    } catch (err) {
      toast.error(err.message || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ss-modal-back" onClick={onClose}>
      <form className="ss-modal" onClick={(e) => e.stopPropagation()} onSubmit={save} data-testid="payout-modal">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <h2 style={{ fontSize: 20 }}>{existing ? "Update payout account" : "Add payout account"}</h2>
          <button type="button" className="disc-search" style={{ width: 34, height: 34 }} onClick={onClose} data-testid="payout-close"><X size={18} /></button>
        </div>
        <p className="ss-muted" style={{ fontSize: 13, marginBottom: 16 }}>Where should we send your earnings? Details are stored securely.</p>

        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          <button type="button" className={`ss-chip ${method === "bank" ? "active" : ""}`} onClick={() => setMethod("bank")} data-testid="payout-method-bank"><Building2 size={14} /> Bank account</button>
          <button type="button" className={`ss-chip ${method === "paypal" ? "active" : ""}`} onClick={() => setMethod("paypal")} data-testid="payout-method-paypal"><CreditCard size={14} /> PayPal</button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label className="ss-label">Account holder name</label>
            <div className="ss-field"><input placeholder="Full name" value={accountName} onChange={(e) => setAccountName(e.target.value)} data-testid="payout-name" /></div>
          </div>
          {method === "bank" ? (
            <>
              <div>
                <label className="ss-label">Bank name</label>
                <div className="ss-field"><Building2 size={16} className="ss-muted" /><input placeholder="e.g. Chase" value={bankName} onChange={(e) => setBankName(e.target.value)} data-testid="payout-bank" /></div>
              </div>
              <div>
                <label className="ss-label">Account number</label>
                <div className="ss-field"><Banknote size={16} className="ss-muted" /><input placeholder="Account number" value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} data-testid="payout-number" /></div>
              </div>
            </>
          ) : (
            <div>
              <label className="ss-label">PayPal email</label>
              <div className="ss-field"><CreditCard size={16} className="ss-muted" /><input type="email" placeholder="you@example.com" value={paypalEmail} onChange={(e) => setPaypalEmail(e.target.value)} data-testid="payout-paypal" /></div>
            </div>
          )}
          <button className="ss-btn ss-btn-primary ss-btn-block" disabled={saving} data-testid="payout-save">{saving ? "Saving…" : "Save payout account"}</button>
        </div>
      </form>
    </div>
  );
}

function PayoutDetailModal({ payoutId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/finances/payouts/${payoutId}`).then(setData).catch(() => setData({ orders: [] })); }, [payoutId]);
  return (
    <div className="ss-modal-back" onClick={onClose}>
      <div className="ss-modal" onClick={(e) => e.stopPropagation()} data-testid="payout-detail-modal">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <h2 style={{ fontSize: 20 }}>Payout details</h2>
          <button type="button" className="disc-search" style={{ width: 34, height: 34 }} onClick={onClose} data-testid="payout-detail-close"><X size={18} /></button>
        </div>
        {!data ? <Loading /> : (
          <>
            <p className="ss-muted" style={{ fontSize: 13, marginBottom: 14 }}>
              {money(data.payout?.amount)} · {new Date(data.payout?.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })} · {(data.orders || []).length} order{(data.orders || []).length === 1 ? "" : "s"}
            </p>
            {(data.orders || []).length === 0 ? (
              <div className="ss-muted" style={{ fontSize: 14 }}>No order details available for this payout.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 360, overflow: "auto" }}>
                {data.orders.map((o) => (
                  <div key={o.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "10px 12px", background: "var(--gold-soft, #f7f5ef)", borderRadius: 10 }} data-testid={`payout-order-${o.id}`}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{o.item_title}</div>
                      <div className="ss-muted" style={{ fontSize: 12 }}>{o.buyer_name} · gross {money(o.amount)}</div>
                    </div>
                    <div style={{ fontWeight: 800, color: "var(--success)" }}>{money(o.seller_net)}</div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function Finances() {
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [payout, setPayout] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [connect, setConnect] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [paying, setPaying] = useState(false);
  const [payouts, setPayouts] = useState([]);
  const [detailId, setDetailId] = useState(null);
  const [showCountry, setShowCountry] = useState(false);

  const load = () => Promise.all([api.get("/seller/summary"), api.get("/seller/orders"), api.get("/seller/payout"), api.get("/connect/status"), api.get("/finances/payouts")])
    .then(([s, o, p, c, ph]) => { setSummary(s); setOrders(o.orders); setPayout(p.payout_account); setConnect(c); setPayouts(ph.payouts || []); }).catch(() => {});
  useEffect(() => { load(); }, []);

  // Handle return from Stripe hosted onboarding (return_url=/finances?connect=done|refresh).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connectFlag = params.get("connect");
    if (connectFlag === "done") toast.success("Thanks! We're finishing your Stripe verification — payouts unlock once it's approved.");
    else if (connectFlag === "refresh") toast.info("Your Stripe link expired — tap Verify with Stripe to continue.");
    if (connectFlag) {
      window.history.replaceState({}, "", window.location.pathname);
      load();
    }
  }, []);
  if (!summary) return <Loading />;

  const withdraw = async () => {
    try { const res = await api.post("/seller/payout/withdraw"); toast.success(res.message); }
    catch (e) { toast.error(e.message); }
  };

  const connectStripe = async (country) => {
    setConnecting(true);
    try {
      const body = { origin_url: window.location.origin };
      if (country) body.country = country;
      const res = await api.post("/connect/onboard", body);
      if (res.onboarding_url) { window.location.href = res.onboarding_url; }
      else { toast.error("Could not start Stripe onboarding"); setConnecting(false); }
    } catch (e) { toast.error(e.message || "Could not connect to Stripe"); setConnecting(false); }
  };

  // First-time onboarding needs a payout country (set once). Resuming an existing account skips it.
  const startConnect = () => {
    if (connect?.stripe_connected) connectStripe();
    else setShowCountry(true);
  };

  const stripePayout = async () => {
    setPaying(true);
    try { const res = await api.post("/finances/payout"); toast.success(res.message); load(); }
    catch (e) { toast.error(e.message || "Payout failed"); }
    finally { setPaying(false); }
  };

  const connReady = connect?.connected;
  const connStarted = connect?.stripe_connected;

  return (
    <div className="fade-up">
      <PageHead title="Finances" sub="Your balances and payout history. SouqStream keeps a small platform fee per sale." />

      <div className="ss-grid grid-3">
        <div className="ss-card stat-card"><div className="stat-ic"><Wallet size={22} /></div><div><div className="stat-val">{money(summary.available)}</div><div className="stat-label">Available to payout</div></div></div>
        <div className="ss-card stat-card"><div className="stat-ic"><Clock size={22} /></div><div><div className="stat-val">{money(summary.pending)}</div><div className="stat-label">Pending (in transit)</div></div></div>
        <div className="ss-card stat-card"><div className="stat-ic"><TrendingUp size={22} /></div><div><div className="stat-val">{money(summary.lifetime)}</div><div className="stat-label">Lifetime net earnings</div></div></div>
      </div>

      {/* Stripe Connect — instant bank payouts */}
      <div className="ss-card" style={{ padding: 22, marginTop: 20 }} data-testid="stripe-connect-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div className="stat-ic" style={{ background: connReady ? "rgba(16,185,129,.12)" : undefined }}>
              {connReady ? <CheckCircle2 size={22} color="var(--success)" /> : <Zap size={22} />}
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16 }}>Stripe payouts & seller verification {connReady && <span className="ss-muted" style={{ fontWeight: 600, fontSize: 13 }}>· Verified</span>}</div>
              {connReady ? (
                <div className="ss-muted" style={{ fontSize: 14, marginTop: 2 }} data-testid="connect-ready">You're a <b>verified seller</b> ✓ — buyers see your verified badge, and you can withdraw earnings straight to your bank.</div>
              ) : connStarted ? (
                <div style={{ fontSize: 14, marginTop: 2, color: "var(--warning, #b45309)", display: "flex", alignItems: "center", gap: 6 }} data-testid="connect-incomplete"><AlertCircle size={14} /> Verification started — finish it to enable payouts and earn your verified-seller badge{connect?.currently_due?.length ? ` (${connect.currently_due.length} items left)` : ""}.</div>
              ) : (
                <div className="ss-muted" style={{ fontSize: 14, marginTop: 2 }}>Verify your identity with Stripe to unlock fast payouts to your bank <b>and earn the verified-seller badge</b> buyers trust.</div>
              )}
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            {connReady ? (
              <button className="ss-btn ss-btn-primary ss-btn-sm" onClick={stripePayout} disabled={paying || summary.available <= 0} data-testid="stripe-withdraw-button"><Banknote size={14} /> {paying ? "Sending…" : `Withdraw ${money(summary.available)}`}</button>
            ) : (
              <button className="ss-btn ss-btn-primary" onClick={startConnect} disabled={connecting} data-testid="connect-stripe-button"><Zap size={16} /> {connecting ? "Connecting…" : connStarted ? "Finish verification" : "Verify with Stripe"}</button>
            )}
          </div>
        </div>
        {connect?.country && (
          <div data-testid="payout-country-lock" style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 14, padding: "10px 14px", background: "var(--gold-soft)", borderRadius: 10, fontSize: 13.5 }}>
            <Lock size={14} style={{ color: "var(--gold-2)", flexShrink: 0 }} />
            <span>Payout country: <b>{countryName(connect.country)}</b> <span className="ss-muted">· set once and locked. <Link to="/support" style={{ color: "var(--brand)", fontWeight: 700 }}>Contact support</Link> to change it.</span></span>
          </div>
        )}
        {!connReady && !connect?.country && (
          <div data-testid="payout-countries-note" style={{ marginTop: 14, padding: "12px 14px", background: "var(--surface-3, #f0eee6)", borderRadius: 10, fontSize: 13 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700 }}>
              <Globe size={15} style={{ color: "var(--gold-2)", flexShrink: 0 }} /> Payouts are supported in {PAYOUT_COUNTRIES.length} countries.
            </div>
            <details style={{ marginTop: 6 }}>
              <summary style={{ cursor: "pointer", color: "var(--brand)", fontWeight: 700, fontSize: 12.5 }} data-testid="payout-countries-toggle">See supported payout countries</summary>
              <p className="ss-muted" style={{ margin: "8px 0 0", lineHeight: 1.6 }}>{PAYOUT_COUNTRY_NAMES}.</p>
              <p className="ss-muted" style={{ margin: "6px 0 0" }}>Some places we ship to — including <b>India</b> and <b>Lebanon</b> — aren't supported by Stripe for payouts yet. You pick your payout country when you verify, and it's <b>set once</b>.</p>
            </details>
          </div>
        )}
      </div>

      {/* Payout account (manual bank/PayPal — legacy) */}
      <div className="ss-card" style={{ padding: 22, marginTop: 20 }} data-testid="payout-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div className="stat-ic"><CreditCard size={22} /></div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16 }}>Payout account</div>
              {payout ? (
                <div className="ss-muted" style={{ fontSize: 14, marginTop: 2 }} data-testid="payout-summary">
                  {payout.method === "bank"
                    ? `${payout.bank_name} •••• ${payout.last4} · ${payout.account_name}`
                    : `PayPal · ${payout.paypal_email}`}
                </div>
              ) : (
                <div className="ss-muted" style={{ fontSize: 14, marginTop: 2 }}>No payout account yet — add one to receive your earnings.</div>
              )}
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            {payout ? (
              <>
                <button className="ss-btn ss-btn-ghost ss-btn-sm" onClick={() => setShowModal(true)} data-testid="edit-payout"><Pencil size={14} /> Update</button>
                <button className="ss-btn ss-btn-primary ss-btn-sm" onClick={withdraw} data-testid="withdraw-button"><Banknote size={14} /> Withdraw {money(summary.available)}</button>
              </>
            ) : (
              <button className="ss-btn ss-btn-primary" onClick={() => setShowModal(true)} data-testid="add-payout-button"><Plus size={16} /> Add payout account</button>
            )}
          </div>
        </div>
      </div>

      <h2 style={{ fontSize: 20, margin: "26px 0 12px" }}>Payout history</h2>
      {payouts.length === 0 ? (
        <Empty icon={Banknote} title="No payouts yet" sub="Withdraw your available balance to see your Stripe payouts here." />
      ) : (
        <div className="ss-card" style={{ overflow: "auto", marginBottom: 8 }} data-testid="payout-history">
          <table className="ss-table" data-testid="payout-history-table">
            <thead><tr><th>Date</th><th>Amount</th><th>Orders</th><th>Status</th></tr></thead>
            <tbody>
              {payouts.map((p) => (
                <tr key={p.id} data-testid={`payout-row-${p.id}`} onClick={() => setDetailId(p.id)} style={{ cursor: "pointer" }} title="View orders in this payout">
                  <td>{new Date(p.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}</td>
                  <td style={{ fontWeight: 800, color: "var(--success)" }}>{money(p.amount)}</td>
                  <td>{(p.order_ids || []).length}</td>
                  <td><StatusPill status={p.status || "paid"} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h2 style={{ fontSize: 20, margin: "26px 0 12px" }}>Recent sales</h2>
      {orders.length === 0 ? (
        <Empty icon={Wallet} title="No earnings yet" sub="Your payouts appear here after your first sale." />
      ) : (
        <div className="ss-card" style={{ overflow: "auto" }}>
          <table className="ss-table" data-testid="finances-table">
            <thead><tr><th>Item</th><th>Gross</th><th>Net to you</th><th>Status</th></tr></thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id}>
                  <td style={{ fontWeight: 700 }}>{o.item_title}</td>
                  <td>{money(o.amount)}</td>
                  <td style={{ fontWeight: 800, color: "var(--success)" }}>{money(o.seller_net)}</td>
                  <td><StatusPill status={o.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <PayoutModal
          existing={payout}
          onClose={() => setShowModal(false)}
          onSaved={(acct) => { setPayout(acct); setShowModal(false); }}
        />
      )}
      {detailId && <PayoutDetailModal payoutId={detailId} onClose={() => setDetailId(null)} />}
      {showCountry && (
        <ConnectCountryModal
          onClose={() => setShowCountry(false)}
          onContinue={connectStripe}
        />
      )}
    </div>
  );
}
